import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Flame, TrendingUp } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ArticleCard } from "@/components/ArticleCard";
import { getTrendingDiscoveries, getCategoriesHub, type DBCategory, type DBDiscovery, type TrendingItem } from "@/lib/content.functions";
import { resolveImage } from "@/lib/asset-map";

export const Route = createFileRoute("/trending")({
  loader: async ({ context }) => {
    const [trending, hub] = await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["trending-page"],
        queryFn: () => getTrendingDiscoveries({ data: { limit: 20 } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["categories-hub"],
        queryFn: () => getCategoriesHub(),
      }),
    ]);
    return { trending, hub };
  },
  head: () => {
    const url = "https://smartasaurus.com/trending";
    const title = "Trending Discoveries · Smartasaurus";
    const desc = "The most-read Smartasaurus articles right now - updated continuously from real reader views over the last 7 days.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: TrendingPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-muted-foreground">Couldn't load trending.</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground">Retry</button>
        </div>
      </div>
    );
  },
});

function TrendingPage() {
  const { trending, hub } = Route.useLoaderData() as {
    trending: { items: TrendingItem[]; source: "views" | "fallback" };
    hub: { categories: DBCategory[]; discoveries: DBDiscovery[] };
  };
  const catLabel = (id: string | null) =>
    hub.categories.find((c) => c.id === id)?.label ?? "Discovery";
  const catSlug = (id: string | null) =>
    hub.categories.find((c) => c.id === id)?.slug ?? null;

  const top = trending.items.slice(0, 10);
  const rising = trending.items.slice(10, 16);

  // Group all trending by category for "Trending by Category"
  const byCategory = new Map<string, TrendingItem[]>();
  for (const t of trending.items) {
    const id = t.category_id ?? "_";
    if (!byCategory.has(id)) byCategory.set(id, []);
    byCategory.get(id)!.push(t);
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-24">
        <div className="border-b border-border pb-6">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
            <Flame className="h-3 w-3" /> Trending · Last 7 days
          </div>
          <h1 className="mt-2 font-display text-4xl uppercase leading-none md:text-6xl">
            What people are reading now
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Ranked by real reader views from the last week.{" "}
            {trending.source === "fallback" && (
              <span className="italic">Showing fresh picks until enough views accumulate.</span>
            )}
          </p>
        </div>

        {top.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 font-display text-2xl uppercase md:text-3xl">Top 10 Trending</h2>
            <ol className="divide-y divide-border border-y border-border">
              {top.map((d, i) => (
                <li key={d.id}>
                  <Link
                    to="/discovery/$slug"
                    params={{ slug: d.slug }}
                    className="group grid grid-cols-[3rem_1fr] items-center gap-5 py-5 transition hover:bg-card md:grid-cols-[4rem_140px_1fr]"
                  >
                    <span className="font-display text-3xl text-primary md:text-5xl">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="hidden aspect-[4/3] overflow-hidden rounded-xl bg-muted md:block">
                      {d.image_url && (
                        <img
                          src={resolveImage(d.image_url)}
                          alt={d.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
                        · {catLabel(d.category_id)}
                      </div>
                      <h3 className="mt-1 font-display text-xl uppercase leading-tight text-foreground group-hover:text-primary md:text-2xl">
                        {d.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{d.excerpt}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </section>
        )}

        {rising.length > 0 && (
          <section className="mt-16">
            <div className="mb-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              <TrendingUp className="h-3.5 w-3.5" /> Rising fast
            </div>
            <h2 className="mb-5 font-display text-2xl uppercase md:text-3xl">Climbing the charts</h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3">
              {rising.map((d) => (
                <ArticleCard
                  key={d.id}
                  slug={d.slug}
                  img={resolveImage(d.image_url)}
                  tag={catLabel(d.category_id)}
                  title={d.title}
                  excerpt={d.excerpt}
                  views={d.views_label ?? undefined}
                />
              ))}
            </div>
          </section>
        )}

        {byCategory.size > 1 && (
          <section className="mt-20 border-t border-border pt-10">
            <h2 className="mb-6 font-display text-2xl uppercase md:text-3xl">Trending by Category</h2>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {hub.categories.map((c) => {
                const items = (byCategory.get(c.id) ?? []).slice(0, 3);
                if (items.length === 0) return null;
                return (
                  <div key={c.id} className="rounded-2xl border border-border bg-card p-5">
                    <div className="mb-3 flex items-end justify-between">
                      <Link to="/category/$slug" params={{ slug: c.slug }} className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary hover:text-foreground">
                        · {c.label}
                      </Link>
                      <Link to="/category/$slug" params={{ slug: c.slug }} className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-primary">
                        Hub →
                      </Link>
                    </div>
                    <ol className="space-y-2">
                      {items.map((d, i) => (
                        <li key={d.id}>
                          <Link
                            to="/discovery/$slug"
                            params={{ slug: d.slug }}
                            className="group flex items-start gap-3"
                          >
                            <span className="font-display text-lg text-primary">{i + 1}.</span>
                            <span className="font-display text-sm uppercase leading-tight text-foreground group-hover:text-primary md:text-base">
                              {d.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ol>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {trending.items.length === 0 && (
          <p className="mt-10 text-muted-foreground">No trending data yet.</p>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
